/**
 * PCCartOrderSync — free gifts first in checkout, matching the cart.
 *
 * Why this exists
 * ---------------
 * The cart page renders free gifts at the top: sections/cart-template.liquid
 * runs its line loop twice, drawing `_gift` rows in the first pass and
 * everything else in the second. The cart drawer groups them the same way.
 * That is presentation only — it does not touch the cart itself.
 *
 * Shopify's checkout renders the order summary in true cart line order, and
 * nothing in a theme can change that: the order summary is not a Liquid
 * template, checkout.liquid is Plus-only and retired, and a Checkout UI
 * extension can render alongside the line list but cannot reorder it. A
 * Shopify Function (cart_transform) can merge and expand lines, not sort them.
 *
 * So the only way to make checkout show gifts first is for them to actually be
 * first in the cart. They never are to begin with: a gift is added by the
 * automation only once a paid item pushes the subtotal past its tier, and
 * /cart/add.js appends.
 *
 * When
 * ----
 * Only in the moment checkout opens — never while the shopper is looking at
 * the cart. Reordering when the gift lands would mean a cart rewrite and a
 * page reload in the middle of shopping, which is a worse experience than the
 * mismatch it fixes. Doing it as the page is being replaced costs the shopper
 * nothing: two requests they never see, on a navigation that was already
 * happening.
 *
 * This module binds no events. Both checkout surfaces already gate their own
 * click, so each calls sync() from the gate it has rather than growing a
 * second one — see "Who calls this" at the foot of the file.
 *
 * That timing is also what keeps the cart page safe. A reorder moves every
 * paid line, and this page addresses lines by 1-based position —
 * updateLineProperties() in sections/cart-template.liquid says so outright:
 * "Line position stays stable for the duration of the page." It does, because
 * nothing here rewrites the cart until the page is on its way out.
 *
 * How
 * ---
 * Shopify has no "reorder lines" call. A line's position is when it was added,
 * and re-adding a variant already in the cart merges into its existing line
 * rather than moving it. The only lever is remove-then-add: drop the paid
 * lines and add them back, in their existing relative order, so they land
 * after the gifts.
 *
 *     before   [ diamond, earrings, GIFT ]
 *     remove   [ GIFT ]
 *     re-add   [ GIFT, diamond, earrings ]
 *
 * Safety
 * ------
 * Between the remove and the add the shopper's paid items are not in the cart.
 * A failed re-add would empty their bag, so the add is retried as a batch, then
 * item by item (a batch /cart/add.js is all-or-nothing — falling back to
 * singles recovers whatever can still be added), and a cart that does not come
 * back whole sends the shopper to /cart to see what survived rather than into
 * checkout with a short order.
 *
 * Load order: after pc-cart-store.js and pc-duplicate-guard.js. The removal
 * runs first, so by the time the re-add reaches the duplicate guard those
 * variants are gone from the cart and it lets them through; the explicit
 * PCCartStore.invalidate() below makes sure the guard decides on a cart read
 * taken after the removal, not the cached one from before it.
 */
window.PCCartOrderSync = (function () {
  'use strict';

  var GIFT_PROPERTY = '_gift';

  // sections/cart-template.liquid's gift reconciler holds this while it is
  // adding or removing gift lines. Reordering across that would have both
  // sides writing the cart at once, which is what drove the cart page to own
  // gift mutations in the first place (see theme.js updateElements).
  var GIFT_LOCK = 'gift_lock';

  var _inFlight = null;

  // ── Cart reads ─────────────────────────────────────────────────────────────

  function readCart(fresh) {
    if (window.PCCartStore) {
      return fresh ? window.PCCartStore.refresh() : window.PCCartStore.get();
    }
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  // Deliberately the cart page's test — `item.properties._gift == 'true'` in
  // sections/cart-template.liquid — and not the looser truthiness check the
  // drawer and the gift reconciler use. The cart page's row order is the one
  // being mirrored into checkout, so a line it draws as paid has to be treated
  // as paid here, whatever some other rule would call it.
  function isGift(item) {
    return !!item && !!item.properties && String(item.properties[GIFT_PROPERTY]) === 'true';
  }

  // Out of order the moment a gift sits behind anything paid. Checking for a
  // gift-first prefix rather than comparing against a sorted copy keeps the
  // common answer — "already fine" — to one pass and no allocation.
  function needsReorder(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    var seenPaid = false;
    for (var i = 0; i < cart.items.length; i++) {
      if (isGift(cart.items[i])) {
        if (seenPaid) return true;
      } else {
        seenPaid = true;
      }
    }
    return false;
  }

  // Everything /cart/add.js needs to rebuild a line exactly as it was. Line
  // item properties are the whole point of most of these lines — ring size,
  // engraving, the builder's hidden _sku and _stone_type — so a line rebuilt
  // without them is not the same line.
  function addPayload(item) {
    var payload = {
      id: item.variant_id,
      quantity: item.quantity,
      properties: item.properties || {}
    };
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
      payload.selling_plan = item.selling_plan_allocation.selling_plan.id;
    }
    return payload;
  }

  // ── Cart writes ────────────────────────────────────────────────────────────

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) {
        var error = new Error(url + ' -> ' + response.status);
        error.status = response.status;
        error.retryAfter = response.headers.get('Retry-After');
        throw error;
      }
      return response.json();
    });
  }

  function wait(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  // Re-posts an add that came back 429 Too Many Requests: waits Retry-After
  // (default 2 s), doubling each time plus jitter, for at most 4 attempts and
  // ~15 s of waiting. Any other failure is passed straight back - an immediate
  // retry of a 422 or a network error only repeats it.
  function addLinesPolitely(payloads) {
    var attempt = 0;
    var waited = 0;
    function tryOnce() {
      attempt++;
      return addLines(payloads).catch(function (error) {
        if (!error || error.status !== 429 || attempt >= 4) throw error;
        var seconds = Number(error.retryAfter);
        var base = isFinite(seconds) && seconds >= 0 && error.retryAfter !== null ? seconds * 1000 : 2000;
        var delay = Math.min(base * Math.pow(2, attempt - 1) + Math.floor(Math.random() * 400), 15000 - waited);
        if (delay < 250) throw error;
        waited += delay;
        return wait(delay).then(tryOnce);
      });
    }
    return tryOnce();
  }

  // One /cart/update.js keyed by line rather than a /cart/change.js each: the
  // cart is emptied of paid lines in a single round trip, which is the window
  // in which the shopper's bag is incomplete.
  function removeLines(items) {
    var updates = {};
    items.forEach(function (item) { updates[item.key] = 0; });
    return postJson('/cart/update.js', { updates: updates });
  }

  function addLines(payloads) {
    return postJson('/cart/add.js', { items: payloads });
  }

  // Best effort after a failed batch. Order is preserved by adding in sequence
  // rather than in parallel — a Promise.all here would let the requests land in
  // any order, which is the one thing this whole module exists to control.
  function addOneByOne(payloads) {
    return payloads.reduce(function (chain, payload) {
      return chain.then(function (added) {
        return addLinesPolitely([payload])
          .then(function () { return added + 1; })
          .catch(function () { return added; });
      });
    }, Promise.resolve(0));
  }

  // ── The reorder ────────────────────────────────────────────────────────────

  function reorder(cart) {
    var paid = cart.items.filter(function (item) { return !isGift(item); });

    // Every paid line moves, not just the ones ahead of a gift. Re-adding only
    // the lines that sit before a gift would fix the gift's position and
    // shuffle the paid lines against each other — [diamond, GIFT, earrings]
    // would come back [GIFT, earrings, diamond]. Moving them all keeps their
    // relative order intact.
    var payloads = paid.map(addPayload);
    var expected = cart.items.length;

    return removeLines(paid)
      .then(function () {
        if (window.PCCartStore) window.PCCartStore.invalidate();
        // A 429 from the write that just went out is waited out (it used to be
        // retried at once, which only met another 429). Anything else, or a 429
        // that outlasts the backoff, falls back to adding line by line.
        return addLinesPolitely(payloads).catch(function () {
          return addOneByOne(payloads);
        });
      })
      .then(function () {
        if (window.PCCartStore) window.PCCartStore.invalidate();
        return readCart(true);
      })
      .then(function (after) {
        var whole = after && Array.isArray(after.items) && after.items.length === expected;
        return { changed: true, ok: !!whole, cart: after };
      });
  }

  /**
   * Put gifts first in the real cart if they are not already.
   *
   * Call this only when checkout is certain to open — it rewrites paid lines,
   * which invalidates every line position the cart page holds.
   *
   * Resolves { changed, ok }. `changed` is false when the cart was already in
   * order — the overwhelmingly common case, and the only one that costs
   * nothing. `ok` is false only when the cart came back with fewer lines than
   * it went in with, which callers must treat as "do not proceed to checkout".
   */
  function sync() {
    if (_inFlight) return _inFlight;

    // Cached read first. Every checkout click runs this, and the answer is
    // almost always "already in order" — which PCCartStore can give for free.
    // Only a cache that says otherwise is worth a request, and it is then
    // confirmed against a fresh cart before anything is rewritten: reordering
    // off a stale read would tear up a cart that never needed touching.
    _inFlight = readCart(false)
      .then(function (cached) {
        if (!needsReorder(cached)) return { changed: false, ok: true, cart: cached };

        // The cart page's gift reconciler is mid-write. It will add or remove
        // lines and land the cart somewhere else entirely, so there is nothing
        // worth reordering yet — checkout opens with the order it would have
        // shown anyway, which is the mismatch this fixes, not a broken cart.
        try {
          if (sessionStorage.getItem(GIFT_LOCK)) {
            return { changed: false, ok: true, cart: cached };
          }
        } catch (e) { /* private mode — carry on */ }

        return readCart(true).then(function (cart) {
          if (!needsReorder(cart)) return { changed: false, ok: true, cart: cart };
          return reorder(cart);
        });
      })
      .catch(function (err) {
        console.warn('[PCCartOrderSync] sync failed:', err && err.message);
        // A read that never happened has changed nothing, so the cart is
        // whole and the shopper is not blocked — checkout just shows the
        // order it would have shown anyway.
        return { changed: false, ok: true };
      })
      .then(function (result) {
        _inFlight = null;
        return result;
      });

    return _inFlight;
  }

  // ── Who calls this ─────────────────────────────────────────────────────────
  //
  // Nothing in here listens for a click. This module binds no events at all —
  // it is a service the two existing checkout gates call, each of which
  // already swallows the click, waits, and re-dispatches:
  //
  //   • the cart page — form#pc-cart-form's submit handler in
  //     sections/cart-template.liquid, after its zero-total check passes;
  //   • the drawer and popup — wireCheckoutGate() in
  //     snippets/cart-delivery-estimate.liquid, a capture-phase gate on
  //     .js-cart-btn-checkout.
  //
  // An earlier version added a third capture-phase gate of its own, and the
  // drawer's checkout button stopped working: two gates on document, each
  // swallowing the click and re-dispatching it with its own pass-through flag,
  // neither recognising the other's. The delivery gate's re-dispatched click
  // looked unhandled to this one, which cancelled it — and this one's replay
  // restarted the delivery gate. The button did nothing at all.
  //
  // One gate per surface. A new checkout entry point calls sync() from
  // whatever already gates it rather than adding another listener.

  return {
    sync: sync,
    needsReorder: needsReorder
  };
})();
